/**
 * useCustomTabEntityReconcile — entity-removal → custom-tab reconcile.
 *
 * Diffs the compiled model's entity-id set across recompiles and offers each
 * removed ref to the custom tabs' PanelKind `onEntityRemoved` (the store closes
 * the ones that say "close"). The store owns the tab list; this only computes
 * what left the model. Shared by every workbench host that mounts custom
 * (plugin) tabs — the viewer and Studio.
 */
import { useEffect, useRef } from "react";
import type { ArchitectureModel } from "@archlang/scene/arch-model";
import { useWorkspace } from "./WorkspaceContext.js";
import { useModelIndexes } from "../hooks/useModelIndexes.js";
import type { RemovedEntityRef } from "./plugins/types.js";

export function useCustomTabEntityReconcile(model: ArchitectureModel | null): void {
  const store = useWorkspace();
  const modelIndexes = useModelIndexes(model);
  const prevEntityKeysRef = useRef<Map<string, RemovedEntityRef["kind"]> | null>(null);
  useEffect(() => {
    if (!model || !modelIndexes) return;
    const cur = new Map<string, RemovedEntityRef["kind"]>();
    for (const id of modelIndexes.moduleById.keys()) cur.set(`module\0${id}`, "module");
    for (const id of modelIndexes.processById.keys()) cur.set(`process\0${id}`, "process");
    for (const id of modelIndexes.interfaceById.keys()) cur.set(`interface\0${id}`, "interface");
    for (const ty of model.types) cur.set(`type\0${ty.name}`, "type");
    // Key by stable id when present — a bare-name key collapsed owner-scoped
    // same-named views into one entity, so removing one twin went undetected
    // (r35 class).
    for (const v of model.views) cur.set(`view\0${v.id ?? v.name}`, "view");
    const prev = prevEntityKeysRef.current;
    prevEntityKeysRef.current = cur;
    if (!prev) return; // first model — nothing removed yet
    const removed: RemovedEntityRef[] = [];
    for (const [key, kind] of prev) {
      if (!cur.has(key)) removed.push({ kind, id: key.slice(key.indexOf("\0") + 1) });
    }
    if (removed.length > 0) store.reconcileCustomTabs(removed);
  }, [model, modelIndexes, store]);
}
